import type { ReactNode } from "react";
import { usePermissions } from "../../hooks/usePermissions";

type PermissionGateProps = {
  children: ReactNode;
  /** Single permission or array of permissions (user needs ANY one) */
  permission?: string | string[];
  /** Array of permissions where user needs ALL */
  requireAll?: string[];
  /** Single role or array of roles (user needs ANY one) */
  role?: string | string[];
  /** Fallback content to show when permission check fails */
  fallback?: ReactNode;
};

/**
 * Component to conditionally render children based on user permissions
 *
 * @example
 * // Show button only if user has write permission
 * <PermissionGate permission="taxonomy.write">
 *   <button>Create</button>
 * </PermissionGate>
 *
 * @example
 * // Show if user has ANY of these permissions
 * <PermissionGate permission={['taxonomy.write', 'taxonomy.delete']}>
 *   <button>Edit</button>
 * </PermissionGate>
 *
 * @example
 * // Show if user has ALL of these permissions
 * <PermissionGate requireAll={['taxonomy.write', 'taxonomy.delete']}>
 *   <button>Advanced Actions</button>
 * </PermissionGate>
 *
 * @example
 * // Show based on role
 * <PermissionGate role="taxonomy.admin">
 *   <button>Admin Panel</button>
 * </PermissionGate>
 */
export default function PermissionGate({
  children,
  permission,
  requireAll,
  role,
  fallback = null,
}: PermissionGateProps) {
  const { hasAnyPermission, hasAllPermissions, hasAnyRole } = usePermissions();

  // Check role-based access
  if (role) {
    const roles = Array.isArray(role) ? role : [role];
    if (!hasAnyRole(roles)) {
      return <>{fallback}</>;
    }
  }

  // Check if user needs ALL permissions
  if (requireAll) {
    if (!hasAllPermissions(requireAll)) {
      return <>{fallback}</>;
    }
  }

  // Check if user needs ANY permission
  if (permission) {
    const permissions = Array.isArray(permission) ? permission : [permission];
    if (!hasAnyPermission(permissions)) {
      return <>{fallback}</>;
    }
  }

  return <>{children}</>;
}
